import { format, parseISO } from 'date-fns';
import { Disclosure } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import Currency from '@v2/shared/components/Currency';
import { useSite } from '@v2/shared/lib/site';
import { cn } from '@v2/shared/lib/utils';
import Box, { BoxLabel } from './Box';
import useAutoRenewalUrls from './urls';
import { AutoRenewalDetail, Transaction as TransactionType } from './types';

const VISIBLE_TRANSACTIONS = 3;

type Props = {
  autoRenewal: AutoRenewalDetail;
  /** One renewal's receipt, with `:orderNumber` where the order's number goes. */
  receiptUrlTemplate: string;
};

/**
 * What this subscription has been charged, newest first, three at a time.
 *
 * A transaction is only shown once it has both a charge date and an order behind it: the
 * others are renewals that were attempted and did not complete, and a customer reading
 * their billing history is looking for money that moved.
 */
export default function BillingHistory({ autoRenewal, receiptUrlTemplate }: Props) {
  const transactions = (autoRenewal.transactions ?? []).filter(
    (transaction) => !!transaction.charged_at && !!transaction.renewal_order
  );

  const firstTransactions = transactions.slice(0, VISIBLE_TRANSACTIONS);
  const additionalTransactions = transactions.slice(VISIBLE_TRANSACTIONS);

  return (
    <>
      <BoxLabel>Billing history</BoxLabel>
      <Box padding="none">
        {transactions.length === 0 && <div className="py-3 px-5 text-center text-slate-600 text-xs">No charges</div>}

        {firstTransactions.map((transaction) => (
          <Transaction
            key={transaction.id}
            transaction={transaction}
            receiptUrlTemplate={receiptUrlTemplate}
          />
        ))}

        {additionalTransactions.length > 0 && (
          <Disclosure defaultOpen={false} as="div" className="text-sm">
            {({ open }) => (
              <>
                {/*
                  * Panel above button, as at the source: the rest of the history unfolds
                  * where the list left off rather than below the control that opened it.
                  */}
                <Disclosure.Panel>
                  {additionalTransactions.map((transaction) => (
                    <Transaction
                      key={transaction.id}
                      transaction={transaction}
                      receiptUrlTemplate={receiptUrlTemplate}
                    />
                  ))}
                </Disclosure.Panel>

                <Disclosure.Button
                  className={cn(
                    open && 'hidden',
                    'bg-transparent border-0 cursor-pointer text-sky-600 hover:underline hover:text-sky-700',
                    'active:text-sky-800 text-xs font-medium text-center w-full p-2'
                  )}
                >
                  Show all
                  <FontAwesomeIcon icon={faAngleDown} className="ml-1.5" />
                </Disclosure.Button>
              </>
            )}
          </Disclosure>
        )}
      </Box>
    </>
  );
}

/**
 * One charge.
 *
 * `parseISO` rather than `new Date` for the same reason the list uses it: these dates come
 * off JMS as an offset with no colon, which is outside what ECMA-262 requires Date.parse
 * to accept, and an engine that declines returns Invalid Date — which date-fns turns into
 * a RangeError thrown mid-render.
 *
 * A refunded row reports `refunded_amount`, not `charged_amount`. headless_jshop printed
 * the amount charged under either label, so a partial refund — £10 back off a £59.99
 * renewal — read as "+$59.99 refunded" and the money actually taken vanished from the
 * history entirely. The label and the number have to agree; this is the only deliberate
 * change to what a row says.
 */
function Transaction({
  transaction,
  receiptUrlTemplate,
}: {
  transaction: TransactionType;
  receiptUrlTemplate: string;
}) {
  const { receiptUrl } = useAutoRenewalUrls();
  const site = useSite();
  const refunded = transaction.refunded_amount > 0;
  const orderNumber = transaction.renewal_order?.number ?? '';

  return (
    <div className="border-b-[1.5px] border-solid border-x-0 border-t-0 last-of-type:border-b-0 border-slate-100 p-2 text-sm flex flex-col md:flex-row md:items-center justify-between">
      <span className={cn('font-medium text-xs mb-3 md:mb-0', refunded && 'text-green-700')}>
        <span className="font-semibold">
          {refunded && '+'}
          <Currency
            value={refunded ? transaction.refunded_amount : transaction.charged_amount}
            currencyCode={site.currency}
          />
        </span>{' '}
        {refunded ? 'refunded' : 'charged'} on{' '}
        {format(parseISO(transaction.charged_at as string), 'MMMM do, yyyy')}
      </span>

      <span className="text-right">
        <a
          target="_blank"
          rel="noreferrer"
          href={receiptUrl(receiptUrlTemplate, orderNumber)}
          className="text-sky-600 hover:underline hover:text-sky-700 active:text-sky-800 text-xs font-medium"
        >
          View receipt
          <FontAwesomeIcon icon={faAngleRight} className="ml-1.5" />
        </a>
      </span>
    </div>
  );
}
